// FIN-11922 / FIN-12130: AFC-uploaded typology names carry a literal "Typology"
// prefix baked into the name itself (e.g. "TypologyHigh count of transactions
// between two parties in a given period of time"). The prefix lives in the
// uploaded source data (tm.typologies / the scenario rule config), so we
// normalise it at the DISPLAY boundary wherever a typology name is shown
// (run-instance + CM Explanation Score Computation, the validation modal, …).
//
// Case note: the persisted prefix is always the capitalised literal "Typology"
// (from the AFC upload), so the match is intentionally case-sensitive — a name
// that legitimately begins with a lowercase "typology…" is left untouched.
export const stripTypologyPrefix = (s) =>
  typeof s === 'string' ? s.replace(/^Typology/, '') : s;

// FIN-13217 / FIN-13228 — shared parser for a stored `typologyNames` field:
// `unified_alerts.TRIGGERED_TYPOLOGY_NAMES` is persisted as a JSON-array string
// with the AFC "Typology" literal glued to each name (see stripTypologyPrefix
// above). Originally lived only in NewAlertsForCustomer.js; CaseFraudAlerts.js
// (FIN-13228) reuses this exact parser rather than re-splitting a joined
// display string — a typology TITLE can itself contain a comma, so recovering
// the individual names by `.split(',')` on the joined text is unsafe. Returns
// the stripped names as an ARRAY; join at the call site for display
// (`parseTypologyNames(raw).join(', ')`), or read `.length` for a count.
export const parseTypologyNames = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map(stripTypologyPrefix);
    return [stripTypologyPrefix(String(parsed))];
  } catch (_) {
    return [stripTypologyPrefix(raw)];
  }
};

// FIN-12883 / FIN-13015 — joined-list variant: `triggered_typology_names` (and the
// queue expand's `typology` column) arrive as either a JSON array or a COMMA-JOINED
// string ("TypologyA, TypologyB"). The anchored regex above only strips the FIRST
// name's baked-in "Typology" prefix, so strip per name -> rejoin. An empty/blank
// list renders `dash`, not a stray ''. Shared by FramlAlertCard (the original
// FIN-12883 fix) and FramlUnderlyingAlerts (FIN-13015 — the one render site that
// never got it).
// [FIN-13015 RCA] — prefer a parsed JSON array over a plain comma split: typology
// titles are free text and can contain commas, which a naive split would
// over-count / mis-split on. Only fall back to a comma split when the value isn't
// valid JSON (matches the FIN-13027 derivation precedent on FramlAlertCard).
export const formatTypologyNames = (val, dash = '—') => {
  if (val == null || String(val).trim() === '') return dash;
  let names;
  try {
    const parsed = JSON.parse(val);
    names = Array.isArray(parsed)
      ? parsed.map(String)
      : String(val).split(',');
  } catch (e) {
    names = String(val).split(',');
  }
  return (
    names
      .map((name) => stripTypologyPrefix(name.trim()))
      .filter(Boolean)
      .join(', ') || dash
  );
};
